import { Component } from 'react';
import PropTypes from 'prop-types';
import { translationPropType } from '@eb/i18n';
import lazygettext from 'js-utils/lazy-gettext';
import { HAS_WINDOW_LISTENERS } from '@eb/feature-detection';

const EVENT_NAME = 'beforeunload';

/**
 * A component that implements browser-native onBeforeUnload functionality.
 * You can include this component in your form and it will automatically
 * wire itself to the window to handle user navigation confirmation.
 * @extends React.Component
 * @prop {bool} shouldConfirm - whether the user should be prompted before allowing navigation
 * @prop {string} message - message of the confirm dialog
 * @example
 * // <ConfirmNavigationBeforeUnload
 * //   shouldConfirm={this.props.dirty}
 * //   message='Are you sure?  Stuff is not saved'
 * // />
 */
export default class ConfirmNavigationBeforeUnload extends Component {
    static propTypes = {
        shouldConfirm: PropTypes.bool,
        message: translationPropType,
    };

    static defaultProps = {
        shouldConfirm: true,
        message: lazygettext(
            "Are you sure you want to leave this page?  Changes you've made may not be saved.",
        ),
    };

    componentDidMount() {
        const { shouldConfirm } = this.props;

        if (shouldConfirm) {
            this._attachHandler();
        }
    }

    componentWillReceiveProps(newProps) {
        const { shouldConfirm } = newProps;

        if (shouldConfirm) {
            this._attachHandler();
        } else {
            this._detachHandler();
        }
    }

    componentWillUnmount() {
        this._detachHandler();
    }

    _attachHandler() {
        if (HAS_WINDOW_LISTENERS) {
            /**
             * Having multiple beforeunload handlers attached will result in only one user prompt.
             * This is a security measure on the part of browsers to prevent abuse.
             * This component can be reused as many times as necessary without side-effect
             * of multiple prompts.
             *
             * Also, if multiple identical eventListeners are registered on the same target with
             * the same parameters, the duplicate instances are discarded.  Therefore, this
             * method can be invoked in the scope of the same component multiple times
             * without side-effect.
             */
            window.addEventListener(EVENT_NAME, this._handleBeforeUnload);
        }
    }

    _detachHandler() {
        if (HAS_WINDOW_LISTENERS) {
            window.removeEventListener(EVENT_NAME, this._handleBeforeUnload);
        }
    }

    _handleBeforeUnload = (event) => {
        const { message } = this.props;

        // eslint-disable-next-line no-param-reassign
        event.returnValue = message;
        return message;
    };

    render() {
        /* this component uses native browser controls (onBeforeUnload dialog) thus renders nothing */
        return null;
    }
}
